import React from 'react';
import s from '../QuickBookModal.module.css';

/**
 * AB#1751 / AB#1752 — Mockup §C + §D labelled stepper.
 *
 * Service-first (mockup §C) — 3 phases:
 *   1 Service · 2 Slot · 3 Confirm
 *
 * Staff-first (mockup §D) — 4 phases, Staff prepended and always "done":
 *   0 Staff · 1 Service · 2 Slot · 3 Confirm
 *
 * The modal internally has 4 states (Category, Service, Slot, Confirm)
 * regardless of mode. The Category and Service internal steps collapse
 * to the "Service" phase here so the visual stepper reads exactly like
 * the mockup.
 */
export type QuickBookPhase = 1 | 2 | 3;

interface StepProgressDotsProps {
    /** Logical phase the user is currently in for the §C 3-phase model. */
    phase: QuickBookPhase;
    /**
     * When true, prepend a "Staff" phase (always done) — the §D entry-point
     * lands the user on Service already, having picked staff via the
     * Calendar's right-click context menu.
     */
    withStaffPhase?: boolean;
}

const SERVICE_FIRST_LABELS: Record<QuickBookPhase, string> = {
    1: 'Service',
    2: 'Slot',
    3: 'Confirm',
};

const StepProgressDots: React.FC<StepProgressDotsProps> = ({ phase, withStaffPhase }) => {
    type Cell = { label: string; state: 'done' | 'current' | 'future' };
    const cells: Cell[] = [];

    if (withStaffPhase) {
        cells.push({ label: 'Staff', state: 'done' });
    }
    ([1, 2, 3] as QuickBookPhase[]).forEach((p) => {
        const state: Cell['state'] =
            p === phase ? 'current' : p < phase ? 'done' : 'future';
        cells.push({ label: SERVICE_FIRST_LABELS[p], state });
    });

    const total = cells.length;
    const ariaValueNow = withStaffPhase ? phase + 1 : phase;
    const currentLabel = cells.find((c) => c.state === 'current')?.label ?? '';

    return (
        <div
            className={s.stepper}
            role="progressbar"
            aria-valuemin={1}
            aria-valuemax={total}
            aria-valuenow={ariaValueNow}
            aria-label={`Step ${ariaValueNow} of ${total} — ${currentLabel}`}
        >
            {cells.map((c, idx) => (
                <React.Fragment key={`${idx}-${c.label}`}>
                    <div className={`${s.step} ${s[`step_${c.state}`]}`}>
                        <div className={s.stepDot}>{idx + 1}</div>
                        <span className={s.stepLabel}>{c.label}</span>
                    </div>
                    {idx < total - 1 && <span className={s.stepSep} />}
                </React.Fragment>
            ))}
        </div>
    );
};

export default StepProgressDots;
